import type { KeyValueStorageInterface } from 'aws-amplify/utils';
import { HUB_SESSION } from '@base-shared/utils/constants';

/**
 * Custom session storage implementation using localStorage.
 * This is used to store session data in a structured way.
 *
 * @implements {KeyValueStorageInterface}
 * The methods defined in this class allow for setting, getting,
 * removing, and clearing items in the session storage.
 *
 * Note that async methods are used to maintain compatibility
 * with the KeyValueStorageInterface, even though localStorage
 * operations are synchronous.
 */

class CustomSessionStorage implements KeyValueStorageInterface {
  storageObject: Storage;
  storageKey = HUB_SESSION;

  constructor() {
    this.storageObject = window.localStorage;
  }

  private get store(): Record<string, string> {
    const raw = this.storageObject.getItem(this.storageKey);
    try {
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  }

  private save(store: Record<string, string>) {
    this.storageObject.setItem(this.storageKey, JSON.stringify(store));
  }

  async setItem(key: string, value: string) {
    const data = this.store;
    data[key] = value;
    this.save(data);
  }

  async getItem(key: string) {
    const data = this.store;
    return data[key] ?? null;
  }

  async removeItem(key: string) {
    const data = this.store;
    const { [key]: _, ...rest } = data;
    this.save(rest);
  }

  async clear() {
    this.storageObject.removeItem(this.storageKey);
  }
}

export const SessionStorage = new CustomSessionStorage();
